import React, { useState } from "react";
import { Form, Button, Row, Col, Upload, Image, theme } from "antd";
import {
  CloudUploadOutlined,
  PictureOutlined,
  GlobalOutlined,
  EyeOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

interface PracticeDataRequestBrandingAssetsProps {
  form: any;
  isEditing: boolean;
  getAdminAssetPreviewUrl: (val: any, assetKey: string) => string;
  onAssetUpload: (field: any, file: File) => void;
}

const PracticeDataRequestBrandingAssets: React.FC<
  PracticeDataRequestBrandingAssetsProps
> = ({ form, isEditing, getAdminAssetPreviewUrl, onAssetUpload }) => {
  const { token } = theme.useToken();
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  return (
    <Row gutter={[16, 16]}>
      {/* 1. Practice Logo Card */}
      <Col xs={24} md={12}>
        <div
          style={{
            background: token.colorBgContainer,
            borderRadius: token.borderRadiusLG,
            padding: "16px 20px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 12,
              marginBottom: 14,
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <div>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: token.colorTextHeading,
                }}
              >
                Practice Logo
              </span>
              <div
                style={{
                  fontSize: 11,
                  color: token.colorTextSecondary,
                  marginTop: 1,
                }}
              >
                Primary practice logo displayed in portal headers and emails.
              </div>
            </div>
          </div>

          <Form.Item
            shouldUpdate={(prev, curr) => prev.logoUrl !== curr.logoUrl}
            noStyle
          >
            {({ getFieldValue }) => {
              const logoVal = getFieldValue("logoUrl");
              const previewUrl = logoVal
                ? getAdminAssetPreviewUrl(logoVal, "logo")
                : null;

              return (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "24px 16px",
                    background: token.colorFillAlter,
                    borderRadius: token.borderRadiusLG,
                    border: `1px dashed ${token.colorBorder}`,
                    minHeight: 160,
                  }}
                >
                  {previewUrl ? (
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                          marginBottom: 12,
                        }}
                      >
                        <img
                          src={previewUrl}
                          alt="Logo Preview"
                          style={{
                            maxHeight: 100,
                            maxWidth: "100%",
                            objectFit: "contain",
                            borderRadius: token.borderRadiusSM,
                            padding: 4,
                            background: token.colorBgContainer,
                            border: `1px solid ${token.colorBorderSecondary}`,
                          }}
                        />
                      </div>
                      <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                        <Button
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={() => setPreviewImage(previewUrl)}
                        >
                          Preview
                        </Button>
                        {isEditing && (
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => form.setFieldValue("logoUrl", "")}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: "center" }}>
                      <PictureOutlined
                        style={{
                          fontSize: 32,
                          color: token.colorTextQuaternary,
                          marginBottom: 8,
                        }}
                      />
                      <p
                        style={{
                          color: token.colorTextSecondary,
                          fontSize: 13,
                          margin: 0,
                        }}
                      >
                        No logo uploaded yet
                      </p>
                    </div>
                  )}

                  {isEditing && (
                    <Upload
                      showUploadList={false}
                      beforeUpload={(file) => {
                        onAssetUpload("logoUrl", file);
                        return Upload.LIST_IGNORE;
                      }}
                    >
                      <Button
                        icon={<CloudUploadOutlined />}
                        style={{ marginTop: 12 }}
                      >
                        {previewUrl ? "Replace Logo" : "Upload Logo"}
                      </Button>
                    </Upload>
                  )}
                </div>
              );
            }}
          </Form.Item>
        </div>
      </Col>

      {/* 2. Favicon Icon Card */}
      <Col xs={24} md={12}>
        <div
          style={{
            background: token.colorBgContainer,
            borderRadius: token.borderRadiusLG,
            padding: "16px 20px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 12,
              marginBottom: 14,
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <div>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: token.colorTextHeading,
                }}
              >
                Favicon Icon
              </span>
              <div
                style={{
                  fontSize: 11,
                  color: token.colorTextSecondary,
                  marginTop: 1,
                }}
              >
                Browser tab icon (recommended dimensions: 32×32 or 48×48).
              </div>
            </div>
          </div>

          <Form.Item
            shouldUpdate={(prev, curr) => prev.faviconUrl !== curr.faviconUrl}
            noStyle
          >
            {({ getFieldValue }) => {
              const faviconVal = getFieldValue("faviconUrl");
              const previewUrl = faviconVal
                ? getAdminAssetPreviewUrl(faviconVal, "favicon")
                : null;

              return (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "24px 16px",
                    background: token.colorFillAlter,
                    borderRadius: token.borderRadiusLG,
                    border: `1px dashed ${token.colorBorder}`,
                    minHeight: 160,
                  }}
                >
                  {previewUrl ? (
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                          marginBottom: 12,
                        }}
                      >
                        <img
                          src={previewUrl}
                          alt="Favicon Preview"
                          style={{
                            height: 48,
                            width: 48,
                            objectFit: "contain",
                            borderRadius: token.borderRadiusSM,
                            padding: 4,
                            background: token.colorBgContainer,
                            border: `1px solid ${token.colorBorderSecondary}`,
                          }}
                        />
                      </div>
                      <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                        <Button
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={() => setPreviewImage(previewUrl)}
                        >
                          Preview
                        </Button>
                        {isEditing && (
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => form.setFieldValue("faviconUrl", "")}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: "center" }}>
                      <GlobalOutlined
                        style={{
                          fontSize: 32,
                          color: token.colorTextQuaternary,
                          marginBottom: 8,
                        }}
                      />
                      <p
                        style={{
                          color: token.colorTextSecondary,
                          fontSize: 13,
                          margin: 0,
                        }}
                      >
                        No favicon uploaded yet
                      </p>
                    </div>
                  )}

                  {isEditing && (
                    <Upload
                      showUploadList={false}
                      beforeUpload={(file) => {
                        onAssetUpload("faviconUrl", file);
                        return Upload.LIST_IGNORE;
                      }}
                    >
                      <Button
                        icon={<CloudUploadOutlined />}
                        style={{ marginTop: 12 }}
                      >
                        {previewUrl ? "Replace Favicon" : "Upload Favicon"}
                      </Button>
                    </Upload>
                  )}
                </div>
              );
            }}
          </Form.Item>
        </div>
      </Col>

      {previewImage && (
        <Image
          wrapperStyle={{ display: "none" }}
          src={previewImage}
          preview={{
            visible: !!previewImage,
            onVisibleChange: (visible) => {
              if (!visible) setPreviewImage(null);
            },
          }}
        />
      )}
    </Row>
  );
};

export default PracticeDataRequestBrandingAssets;
